import { motion } from "framer-motion";
import { X, ExternalLink, CheckCircle2, Clock } from "lucide-react";

export function ArticleReader({ article, onClose, onRead, isClient }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-stone-900/40 backdrop-blur-sm p-4 overflow-y-auto">
      <motion.article
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="warm-card w-full max-w-2xl p-6 sm:p-10 my-6 space-y-5 relative"
        data-testid="article-reader"
      >
        <button
          onClick={onClose}
          data-testid="article-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-800">
          <span>{article.category}</span>
          <span className="text-stone-300">·</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {article.read_minutes} min</span>
          <span className="text-stone-300">·</span>
          <span className="text-stone-500 normal-case tracking-normal">by {article.author_name}</span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-stone-900 leading-tight">{article.title}</h2>
        <p className="text-stone-600 italic">{article.summary}</p>
        <div className="space-y-4 text-stone-800 leading-relaxed">
          {article.body.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`}>{p}</p>)}
        </div>
        {article.sources?.length > 0 && (
          <div className="pt-4 border-t border-stone-100">
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2">Read more (opens outside the app)</div>
            <ul className="space-y-1.5">
              {article.sources.map((s, i) => (
                <li key={s.url || s.title}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`article-source-${i}`}
                    className="inline-flex items-center gap-1.5 text-sm text-amber-800 hover:text-amber-900 font-medium underline-offset-2 hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {isClient && (
          <button
            data-testid="article-mark-read"
            disabled={article.read}
            onClick={() => onRead(article)}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold ${
              article.read ? "bg-emerald-100 text-emerald-800" : "bg-stone-900 text-white hover:bg-amber-700"
            }`}
          >
            <CheckCircle2 className="w-4 h-4" /> {article.read ? "Read ✓" : "I've read this (+5 pts)"}
          </button>
        )}
      </motion.article>
    </div>
  );
}
